<template>
  <div
    :id="mapId"
    class="map-container"
  />
</template>

<script setup>
// @ts-check
import { GOOGLE_MAPS_API_KEY } from '@/js/common/constants';
import {
  onMounted,
  ref,
  useId,
  watch,
} from 'vue';

/** @import { Ref } from 'vue' */

const props = defineProps({
  /** The latitude coordinate for the map center. */
  latitude: {
    type: Number,
    default: 0,
  },
  /** The longitude coordinate for the map center. */
  longitude: {
    type: Number,
    default: 0,
  },
  /** The query to search for on the map. */
  searchQuery: {
    type: String,
    default: '',
  },
});

const mapId = useId();
/** @type {Ref<google.maps.Map | null>} */
const mapInstance = ref(null);
/** @type {Ref<google.maps.places.PlacesService | null>} */
const placesService = ref(null);

watch(() => props.searchQuery, () => {
  searchByQuery();
});

onMounted(async () => {
  if (props.latitude !== null && props.longitude !== null) {
    await initMap();
  }
});

/**
 * Loads the Google Maps script asynchronously and initializes the map.
 * @returns {Promise<void>} A promise that resolves when the map is loaded and initialized.
 */
async function loadGoogleMaps() {
  if (typeof google === 'undefined') {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    document.body.appendChild(script);
    await new Promise((resolve, reject) => {
      script.onload = resolve;
      script.onerror = reject;
    });
  }
}

/**
 * Loads the Google Maps script asynchronously and initializes the map.
 * @returns {Promise<void>} A promise that resolves when the map is loaded and initialized.
 */
async function initMap() {
  await loadGoogleMaps();
  const mapOptions = {
    center: { lat: props.latitude, lng: props.longitude },
    zoom: 14,
    mapTypeId: window.google.maps.MapTypeId.ROADMAP,
  };

  const mapElement = document.getElementById(mapId);
  if (mapElement) {
    mapInstance.value = new window.google.maps.Map(mapElement, mapOptions);

    // eslint-disable-next-line no-new
    new window.google.maps.Marker({
      position: { lat: props.latitude, lng: props.longitude },
      map: mapInstance.value,
    });
  }
}

/**
 * Search for a location by query using the Google Places API.
 * @returns {void}
 */
function searchByQuery() {
  if (!props.searchQuery || !mapInstance.value) {
    return;
  }

  placesService.value ||= new window.google.maps.places.PlacesService(mapInstance.value);

  const request = {
    query: props.searchQuery,
  };

  placesService.value.textSearch(request, (results, status) => {
    if (status === window.google.maps.places.PlacesServiceStatus.OK
      && results
      && results.length > 0
    ) {
      const place = results[0]?.geometry?.location;
      if (place) {
        centerMapToPlace(place);
      }
    }
  });
}

/**
 * Center the map to a specific location.
 * @param {google.maps.LatLng} location - The location to center the map to.
 * @returns {void}
 */
function centerMapToPlace(location) {
  mapInstance.value?.setCenter(location);
}
</script>

<style scoped lang="scss">
.map-container {
  height: 540px;
  width: 100%;
}
</style>
